import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { getBankDetails, createBankAccountLink } from "../../../services/fundraiserService";
import Loader from "../../common/Loader";
import toast from "../../../utils/toast";

const BankAccountDetails = () => {
  const dispatch = useDispatch();
  const [bankDetails, setBankDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLinking, setIsLinking] = useState(false);

  const fetchBankDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await dispatch(getBankDetails())?.unwrap();
      console.log("Bank Account Details API Response:", response);
      if (response?.success === true) {
        // The API returns an array of external_accounts (bank accounts)
        // We'll take the first one or handle empty array
        const accounts = response?.data || [];
        setBankDetails(accounts.length > 0 ? accounts[0] : null);
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      // Silently handle error - don't show toast for initial fetch unless critical
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchBankDetails();
  }, [fetchBankDetails]);

  const handleAddOrUpdateBank = async () => {
    try {
      setIsLinking(true);
      const response = await dispatch(createBankAccountLink())?.unwrap();
      if (response?.success && response?.data?.url) {
        // Redirect to Stripe
        window.location.href = response.data.url;
      } else {
        toast.error("Failed to generate bank account link.");
      }
    } catch (error) {
      console.error("Error creating bank account link:", error);
      toast.error(error?.message || "Something went wrong.");
    } finally {
      setIsLinking(false);
    }
  };

  const formatAccountNumber = (bankData) => {
    if (bankData?.last4) {
      return `XXXX XXXX XXXX ${bankData.last4}`;
    }
    return "XXXX XXXX XXXX XXXX";
  };

  if (isLoading) {
    return (
      <div className="w-full bg-white rounded-xl shadow-lg mt-10">
        <div className="p-6">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full bg-white rounded-xl shadow-lg mt-8 md:mt-10"
      style={{ boxShadow: '0px -6px 16px -6px rgba(0,0,0,0.08), 0px 6px 16px -6px rgba(0,0,0,0.08)' }}
    >
      <div className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <h2 className="text-[16px] font-[500] text-[#303030] font-poppins">Bank Account Details</h2>
          <button
            onClick={handleAddOrUpdateBank}
            disabled={isLinking}
            className={`w-full md:w-auto px-4 py-2 rounded-lg text-white font-poppins text-[14px] font-[500] transition-colors ${isLinking ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#00ADEF] hover:bg-[#009bd6]'
              }`}
          >
            {isLinking ? "Processing..." : (bankDetails ? "Update Bank Account" : "Add Bank Account")}
          </button>
        </div>
        <hr className="border-t border-[#E9ECEF] mb-6" />

        {bankDetails ? (
          <div className="flex flex-col gap-4">
            <div
              className="w-full rounded-lg border border-[#00ADEF] bg-[#EAF8FE] p-4 flex justify-between items-center"
            >
              <div>
                <div className="flex flex-col gap-1 md:gap-0">
                  <div className="text-[16px] font-[600] font-poppins text-[#303030]">
                    {bankDetails?.bank_name || "Bank Account"}
                  </div>
                  <div className="text-[14px] font-[400] font-poppins text-[#8C8C8C]">
                    {formatAccountNumber(bankDetails)}
                  </div>
                  {/* Status display if available */}
                  <div className="text-[12px] font-[400] font-poppins text-[#8C8C8C] mt-1 capitalize">
                    Status: {bankDetails?.status || 'Active'}
                  </div>
                </div>
              </div>
              <span className="text-[#8C8C8C] font-poppins font-[400] text-[18px]">
                {bankDetails?.currency?.toUpperCase() || 'AUD'}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-[14px] font-[400] text-[#8C8C8C] font-poppins mb-4">
              No bank account added yet
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankAccountDetails; 